import type {
  Treatment,
  TreatmentDoctor,
} from "@/features/clinic/types/treatment";

import { createId, createPastTimestamp } from "./base";
import { isDemoRecording } from "../config";

interface TreatmentConfig {
  name: string;
  description: string;
  price: number;
  coverPicture: string;
}

const STORAX_TREATMENTS: TreatmentConfig[] = [
  {
    name: "Laser Hair Removal (Half Legs)",
    description:
      "A laser hair removal solution for lower legs, offering lasting smoothness and minimal maintenance.",
    price: 400,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-woman-making-body-hair-removal-laser-depilation-beauty-center_106892563/public",
  },
  {
    name: "P-Shot + Filler (4 mL)",
    description:
      "Enhanced combination of P-Shot with 4 mL of filler for greater girth and performance results.",
    price: 9000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/p-shot-filler-1-mL/cover/public/public",
  },
  {
    name: "Sculptra + Skin Booster",
    description:
      "A powerful combination that stimulates collagen from deep within (Sculptra) while flooding the skin with intense hydration and glow (Skin Booster). Ideal for restoring firmness, improving fine lines, and achieving long-lasting radiance.",
    price: 4000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/cd82ac15-e615-4fe0-5375-e9630f730c00/public",
  },
  {
    name: "Italian PRP (Platelet-Rich Plasma)",
    description:
      "Innovative platelet-rich plasma therapy to stimulate hair growth and improve scalp health.",
    price: 800,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/bearded-man-getting-anti-hair-loss-treatment-by-beautician_10720222/public",
  },
  {
    name: "Laser (Pink Lips)",
    description:
      "A specialized laser treatment to lighten dark lips and enhance their natural pink tone.",
    price: 300,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/beautiful-woman-getting-facial-skincare-treatment-cosmetic-beauty-clinic_31603225/public",
  },
  {
    name: "Full Checkup Package",
    description:
      "A comprehensive health screening that includes both blood and urine tests to assess key health indicators, detect potential issues early, and provide a clear picture of your overall well-being.",
    price: 975,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/1eda7a97-cb6f-44e3-890a-59fb88f06f00/public",
  },
  {
    name: "Botox (Underarms)",
    description:
      "Reduces excessive sweating by injecting Botox into the affected area.",
    price: 2100,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/j.-hopkins-clinic/treatments/doctor-makes-intramuscular-injections-underarm-area_38163358/public",
  },
  {
    name: "Laser Hair Removal (Beard + Neck)",
    description:
      "A targeted laser hair removal treatment to define beard edges and maintain a clean neckline with long-lasting results.",
    price: 400,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/close-up-female-beautician-hands-sterile-gloves-removing-unwanted-hair-from-male-neck-with-laser-device_16187207/public",
  },
  {
    name: "Lip Blush",
    description:
      "A semi-permanent lip tinting treatment that enhances natural color, defines shape, and creates a soft, youthful flush. Results look natural and last months with minimal downtime.",
    price: 1850,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/99071f62-622c-40f1-2699-bc185a970500/public",
  },
  {
    name: "Nano Facial",
    description:
      "A cutting-edge facial treatment that deeply cleanses, hydrates, and revitalizes your skin, leaving it smooth, radiant, and glowing. Ideal for improving texture and boosting overall skin health.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-woman-lying-cosmetologist-s-table-rejuvenation-procedure_10840463/public",
  },
  {
    name: "Botox (3 Areas) + Soft Light",
    description:
      "Botox smooths wrinkles across three targeted areas (commonly forehead, frown lines, crow's feet), while Soft Light adds a gentle skin-brightening effect and reduces redness. A fast, effective combo for a fresh, youthful look.",
    price: 1200,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/a0517511-a670-47dd-51ac-d13b43ae8c00/public",
  },
  {
    name: "DermaPen with Mesotherapy (3 Sessions)",
    description:
      "Microneedling with nutrient-rich mesotherapy serums to improve texture, fade marks, hydrate deeply, and boost collagen. Three sessions deliver smoother, firmer, more radiant skin.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/0f55ddeb-90dc-47bb-74a3-244b3f4e6000/public",
  },
  {
    name: "Pink Lips (3 Sessions)",
    description:
      "A gentle treatment designed to brighten lip color, reduce pigmentation, and enhance the natural rosy tone of the lips. Performed over three sessions for a soft and natural-looking result.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/f1975e66-0395-4180-8c44-c6e8bd4dc300/public",
  },
  {
    name: "Laser Hair Removal (Beard + Neck + Nape)",
    description:
      "Comprehensive laser grooming that ensures a neat and polished beard, neckline, and nape with no razor irritation.",
    price: 500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/close-up-female-esthetician-hands-sterile-gloves-removing-unwanted-hair-from-male-neck-with-laser-device_16187174/public",
  },
  {
    name: "Botox (Gummy Smile)",
    description:
      "Botox injected to reduce excessive upper lip elevation when smiling.",
    price: 400,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/c967ccff-3b80-45ee-ecbb-c7e853247200/public",
  },
  {
    name: "Mesotherapy",
    description:
      "Microinjections of vitamins and nutrients for skin rejuvenation.",
    price: 1000,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/people-lips-cosmetology-plastic-surgery-beauty-concept-beautiful-young-woman-face-hand-with-syringe-making-injection_12836928/public",
  },
  {
    name: "Exosomes Injection",
    description:
      "Revolutionary therapy using exosomes to stimulate hair regrowth and improve scalp health.",
    price: 3000,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/man-getting-hair-loss-treatment_19966351/public",
  },
  {
    name: "Tattoo Removal (Per Session)",
    description:
      "A non-laser tattoo removal technique that gently breaks down ink particles from the skin's surface using a specialized solution. Each session lightens the tattoo gradually, helping fade unwanted pigment with minimal downtime.",
    price: 800,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/ede98cb1-dc74-4288-e060-dc63e9b6de00/public",
  },
  {
    name: "Body Filler (10 mL)",
    description:
      "Enhances volume and contours in areas such as hips, hands, or décolleté using body-safe dermal filler.",
    price: 10000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/fd0fde4d-e8e2-492a-7dfe-6f3b08f1ed00/public",
  },
  {
    name: "G-Cell Treatment",
    description:
      "Regenerative treatment to enhance hair quality and support healthy growth.",
    price: 4500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/doctor-cosmetologist-making-mesotherapy-injections-woman-s-head-stronger-healthier-hair_10497499/public",
  },
  {
    name: "Laser Hair Removal (Half Arms)",
    description:
      "Gentle and effective laser treatment for silky smooth forearms.",
    price: 300,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-woman-making-body-hair-removal-laser-depilation-beauty-center_106891515/public",
  },
  {
    name: "Men's Health Consultation",
    description:
      "Private consultation to assess and address concerns related to men's sexual health, vitality, and performance.",
    price: 0,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/mens-health-consultation/cover/public/public",
  },
  {
    name: "Botox (3 Areas)",
    description:
      "Smooths fine lines and wrinkles on the forehead, crow's feet, and frown lines for a refreshed appearance.",
    price: 1200,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/cosmetic-injection-portrait-young-pretty-woman-keeping-eyes-closed-while-doctors-hand-blue_17005321/public",
  },
  {
    name: "Dermatology Consultation",
    description:
      "A detailed assessment to determine the best dermatology treatments.",
    price: 0,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/doctor-holding-face-patient_1198225/public",
  },
  {
    name: "Laser Hair Removal (Beard)",
    description:
      "Targets and shapes the beard area using laser technology for a defined look.",
    price: 400,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/bb25c636-5a73-40b1-746a-e008ccf55b00/public",
  },
  {
    name: "Nano Facial + Dermapen",
    description:
      "This dynamic duo combines the deep nourishment of the Nano Facial with the rejuvenating effects of the Dermapen for enhanced skin renewal and radiance.",
    price: 2000,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/woman-making-beauty-procedures-beauty-salon_5578467/public",
  },
  {
    name: "Laser Hair Removal (Beard + Underarms + Bikini)",
    description:
      "A multi-area laser treatment for smooth skin, eliminating unwanted hair in key areas with precision and comfort.",
    price: 900,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/smiling-young-man-lying-pillow-with-one-hand-his-head-having-laser-hair-removal-his-armpit_17613624/public",
  },
  {
    name: "Liposomal Hair or Face (3 Sessions)",
    description:
      "A targeted treatment using liposomal delivery technology to penetrate deeper layers of the skin or scalp. Helps regenerate cells, reduce inflammation, boost hydration, and improve hair or skin strength over a series of three sessions.",
    price: 3600,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/b3e129eb-6120-481d-a2d6-3642b33c0d00/public",
  },
  {
    name: "Tattoo Removal (Medium Area)",
    description:
      "Targeted tattoo removal for medium-sized areas, using state-of-the-art techniques for precision and safety.",
    price: 500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-woman-undergoing-laser-tattoo-removal-procedure-salon-closeup_29486657/public",
  },
  {
    name: "Laser Hair Removal Full Body (Female)",
    description:
      "A complete solution for hair removal across the entire body, tailored for women seeking long-lasting smooth skin.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/beautiful-woman-getting-facial-skincare-treatment-cosmetic-beauty-clinic_31603225/public",
  },
  {
    name: "Botox (1 Area)",
    description:
      "A muscle-relaxing injection that reduces fine lines and wrinkles for a smoother, more youthful look.",
    price: 700,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/common/treatments/cosmetic-injection-portrait-young-pretty-woman-keeping-eyes-closed-while-doctors-hand-blue_17005321/public",
  },
  {
    name: "Botox (Nefertiti Neck)",
    description:
      "Botox along the jawline and neck to tighten skin, define the jaw, and reduce sagging.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/nefertiti-neck/cover/public/public",
  },
  {
    name: "Double P-Shot",
    description:
      "Two rounds of the P-Shot treatment for enhanced and prolonged results.",
    price: 4000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/double-p-shot/cover/public/public",
  },
  {
    name: "IV Drip",
    description:
      "An infusion of essential vitamins, minerals, and antioxidants to boost energy, enhance immunity, and promote overall wellness, tailored to your specific health needs.",
    price: 1200,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/a56c3a83-4429-436f-8709-2a2100892600/public",
  },
  {
    name: "Scalp Micropigmentation (Per Hour)",
    description:
      "Creates the illusion of thicker hair with natural pigments to conceal thinning areas.",
    price: 1800,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/male-baldness-before-after-treatment-portrait-man-with-baldness-problem-hair-transplant-operation-cosmetic-surgery-process-hair-transplantation-head_36727695/public",
  },
  {
    name: "Carbon Laser (Face)",
    description:
      "A deep cleansing and rejuvenating treatment that removes dead skin cells, unclogs pores, and brightens your complexion.",
    price: 600,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/beautiful-woman-getting-carbon-face-peeling-procedure-beauty-salon_17435312/public",
  },
  {
    name: "Laser (Face Softlight)",
    description:
      "A gentle laser treatment that improves skin tone, reduces pigmentation, and minimizes fine lines for a refreshed and rejuvenated appearance.",
    price: 500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/laser-epilation-hair-removal-therapy_9820982/public",
  },
  {
    name: "Spanish PRP (Platelet-Rich Plasma)",
    description:
      "Advanced PRP treatment that boosts hair density and strengthens follicles.",
    price: 500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/woman-getting-scalp-prp-treatment-high-angle_26388252/public",
  },
  {
    name: "P-Shot",
    description:
      "A non-surgical PRP treatment to enhance male sexual performance, sensitivity, and blood flow.",
    price: 2500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/p-shot/cover/public/public",
  },
  {
    name: "Laser Hair Removal (Bikini + Underarms)",
    description:
      "A dual-area laser session for smooth, long-lasting hair reduction. Fast, effective, and ideal for maintaining clean, soft skin with minimal discomfort.",
    price: 700,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/bbe6ef37-9b03-489d-ddd2-154512260a00/public",
  },
  {
    name: "Hair Mesotherapy",
    description:
      "Micro-injections of vitamins and nutrients into the scalp to boost hair health and regrowth.",
    price: 1000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/hair-mesotherapy/cover/public/public",
  },
  {
    name: "Tattoo Removal (Large Area)",
    description:
      "Professional tattoo removal for larger areas, delivering visible results with minimal discomfort.",
    price: 700,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-woman-undergoing-laser-tattoo-removal-procedure-salon-closeup_29486642/public",
  },
  {
    name: "Laser Hair Removal (Bikini)",
    description:
      "Safe and effective laser hair removal for the bikini area, ensuring long-lasting smoothness and comfort.",
    price: 400,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/laser-epilation-cosmetology-beauty-salon-beautiful-brunette-woman-getting-hair-removing-legs_158821120/public",
  },
  {
    name: "Tattoo Removal (Small Area)",
    description:
      "Advanced laser technology to safely and effectively remove small tattoos, restoring clear skin.",
    price: 300,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-woman-undergoing-laser-tattoo-removal-procedure-salon-closeup_75745237/public",
  },
  {
    name: "Hair Transplant",
    description:
      "Restores natural hair growth in balding or thinning areas, delivering permanent and natural-looking results.",
    price: 15000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/b4b65ecc-7b6e-462e-636c-b4a732622f00/public",
  },
  {
    name: "Red Shot (Maxi PRP)",
    description:
      "Improves erection, performance, and treats premature ejaculation.",
    price: 5000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/max-prp/cover/public/public",
  },
  {
    name: "Eyebrow Microblading",
    description:
      "Semi-permanent treatment to shape and fill in eyebrows with precise, hair-like strokes for a flawless finish.",
    price: 3000,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/make-up-master-performs-procedure-permanent-eyebrow-makeup-client_18512635/public",
  },
  {
    name: "Laser Hair Removal (Full Legs)",
    description:
      "A complete hair removal service for your legs, leaving them flawlessly smooth and hair-free.",
    price: 600,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/laser-epilation-cosmetology-beauty-salon-beautiful-brunette-woman-getting-hair-removing-legs_158821120/public",
  },
  {
    name: "Scar Removal (Per Area)",
    description:
      "A specialized treatment to reduce scars caused by acne, injury, or surgery. Improves texture, smoothness, and overall skin appearance through targeted laser or resurfacing techniques.",
    price: 1000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/1227c323-044c-4cf0-8406-eee71e001000/public",
  },
  {
    name: "Botox (Migraine)",
    description:
      "A preventive treatment to reduce the frequency and severity of chronic migraines.",
    price: 1000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/botox-migraine/cover/public/public",
  },
  {
    name: "Eyebrow Transplant",
    description:
      "Creates fuller, well-defined eyebrows with natural hair grafts for a perfect frame to your face.",
    price: 10000,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/client-going-through-microblading-treatment_20553912/public",
  },
  {
    name: "Hair Transplant Consultation",
    description:
      "A 30-minute session with a specialist to assess your scalp, discuss hair restoration options, and create a personalized treatment plan.",
    price: 0,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/23c50183-0c8a-4733-883d-a6b134c63a00/public",
  },
  {
    name: "Collagen Bio-Stimulator (Radiesse)",
    description:
      "Dermal filler that adds volume and stimulates collagen production.",
    price: 2800,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/beautiful-young-woman-getting-botox-cosmetic-injection-her-face_1623515/public",
  },
  {
    name: "Slimming Therapy (Radio Frequency)",
    description:
      "A non-invasive treatment designed to contour your body, reduce stubborn fat, and improve overall skin tone and firmness for a sleeker silhouette, using radio frequency.",
    price: 600,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/procedure-removing-cellulite-female-abdomen-cavitation-belly-massage-ultrasonic-massage-weight-loss-correction-female-figure-without-surgical-intervention-closeup-tummy_23141281/public",
  },
  {
    name: "Dermapen (Microneedling)",
    description:
      "A micro-needling treatment that promotes collagen production, reduces acne scars, and smooths fine lines, giving your skin a rejuvenated and youthful look.",
    price: 850,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/woman-wellness-center-having-cosmetic-treatment_12112255/public",
  },
  {
    name: "Laser Hair Removal Full Body Without Back and Front (Female)",
    description:
      "Hair removal treatment for all areas except the back and front, ensuring convenience and comfort.",
    price: 1200,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/cosmetologist-does-laser-hair-removal-legs_158821227/public",
  },
  {
    name: "Laser Hair Removal Full Body (Male)",
    description:
      "Comprehensive laser hair removal tailored to meet the needs of men for a clean and groomed appearance.",
    price: 2500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/person-rubber-gloves-holding-modern-tool-while-conducting-laser-hair-removal-with-patient_17613630/public",
  },
  {
    name: "Collagen Bio-Stimulator (Rich)",
    description:
      "Premium collagen therapy offering comprehensive skin rejuvenation and enhanced elasticity.",
    price: 4500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/close-up-hands-young-cosmetologist-injecting-botox-female-face-she-is-standing-smiling_352553001/public",
  },
  {
    name: "Skin Booster",
    description:
      "An injectable treatment that hydrates the skin from within, improving elasticity, texture, and overall radiance.",
    price: 2000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/99529172-6f6a-4f3c-137a-80ce68a67000/public",
  },
  {
    name: "Carbon Laser Face + Soft Light (3 Sessions)",
    description:
      "Carbon laser deeply cleans pores, reduces oil, and brightens the skin, while Soft Light calms, tones, and enhances radiance. A three-session program for clearer, smoother, glowing skin.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/3114b70c-3ce1-4c92-8918-5d9280263000/public",
  },
  {
    name: "Laser Hair Removal (Underarms)",
    description:
      "Efficient and painless hair removal for underarms, leaving you with smooth, flawless skin.",
    price: 300,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/doctor-performing-laser-hair-removal-patient-skin_8403641/public",
  },
  {
    name: "Filler (1 mL)",
    description:
      "Injectable dermal fillers to restore volume and enhance facial contours.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/young-beautiful-woman-receiving-injection-filler-lips-injection-cosmetology_11997133/public",
  },
  {
    name: "Hair Transplant (Women)",
    description:
      "Restore your hair with natural, permanent results at ACME Clinic, now at an exclusive ClinicSaver price. Available only on ClinicSaver! (Unlimited grafts).",
    price: 15000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/864da176-b240-41bf-a189-facf07229800/public",
  },
  {
    name: "Laser Hair Removal Half Body (Male)",
    description:
      "Upper or lower body laser treatment for men, offering smooth and hair-free results.",
    price: 1200,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/smiling-young-man-lying-pillow-with-one-hand-his-head-having-laser-hair-removal-his-armpit_17613624/public",
  },
  {
    name: "Skin Booster (Profhilo Structura)",
    description:
      "A deeper hydration and tissue support treatment targeting structural firmness and skin tightening.",
    price: 3000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/ec4b6ffb-8c78-4b30-f72d-db0085e1a000/public",
  },
  {
    name: "Collagen Bio-Stimulator (Sculptra)",
    description:
      "Injectable treatment that stimulates collagen production for natural-looking results.",
    price: 3200,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/people-lips-cosmetology-plastic-surgery-beauty-concept-beautiful-young-woman-face-hand-with-syringe-making-injection_13909553/public",
  },
  {
    name: "Beard Transplant",
    description:
      "Enhances beard density and fills in patches for a thicker, more uniform look.",
    price: 15000,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/male-face_10926256/public",
  },
  {
    name: "NAD+ IV Drip (3 Sessions)",
    description:
      "A revitalizing IV infusion of NAD+, known for enhancing cellular repair, boosting energy, improving focus, and supporting overall wellness. Taken across three sessions for optimal results.",
    price: 3000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/607d5d48-46ae-4f15-b42c-01364ec79600/public",
  },
  {
    name: "Organic Facial + Meso DermaPen",
    description:
      "A gentle organic facial to cleanse and brighten the skin, followed by DermaPen microneedling with mesotherapy serums to boost collagen, improve texture, and enhance absorption for glowing, healthier skin.",
    price: 1500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/b91af110-6de4-4753-d28a-e3b495e9b300/public",
  },
  {
    name: "Hair Transplant Blood Test",
    description:
      "A vital pre-transplant step, this blood test ensures your body is ready and safe for the procedure by checking key health markers for a smooth, successful result.",
    price: 500,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/61c36294-677f-4f0c-8b22-f6ef00e53700/public",
  },
  {
    name: "Jawline Filler",
    description:
      "Enhances the jawline's definition and contour using injectable dermal fillers.",
    price: 4000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/jawline-filler/cover/public/public",
  },
  {
    name: "Laser Hair Removal (Full Arms)",
    description:
      "Full coverage laser hair removal for completely smooth and hair-free arms.",
    price: 500,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/professional-beautician-wearing-rubber-gloves-while-holding-hand-woman-doing-laser-hair-removal-her-arm_15820191/public",
  },
  {
    name: "P-Shot + Filler (1 mL)",
    description:
      "Combines P-Shot with 1 mL filler to improve both function and size enhancement.",
    price: 4000,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/treatments/p-shot-filler-1-mL/cover/public/public",
  },
  {
    name: "Lip Filler + Laser Pink Lips",
    description:
      "A combined lip-enhancement approach: subtle shaping and volume with filler, followed by laser pink lips to brighten lip color and smooth texture. Perfect for achieving fuller, youthful, naturally rosy lips.",
    price: 1300,
    coverPicture:
      "https://imagedelivery.net/mZnj3c-9V1jcM30vMBRCkQ/2efb77dd-ae9e-451e-5c7b-be5566a75c00/public",
  },
  {
    name: "Laser Hair Removal (Upper Lips)",
    description:
      "A quick and precise laser treatment to permanently remove unwanted upper lip hair, leaving the skin smooth and hair-free.",
    price: 250,
    coverPicture:
      "https://imagedelivery.net/CA0eHVmqq3-bI3-0nePuKA/clinics/storax-lifestyle-clinic/treatments/laser-hair-removal-facial-skin_21790337/public",
  },
];

/**
 * Generate mock treatments for ACME Clinic
 */
export function generateTreatments(
  organizationId: string,
  workspaceId: string,
  count: number = 76,
): Treatment[] {
  const treatments: Treatment[] = [];
  const treatmentsToGenerate = Math.min(count, STORAX_TREATMENTS.length);

  for (let i = 0; i < treatmentsToGenerate; i++) {
    const config = STORAX_TREATMENTS[i];
    const timestamp = createPastTimestamp(90 - i);

    treatments.push({
      id: createId(),
      name: config.name,
      description: config.description,
      price: config.price,
      discountedPrice: null,
      // The first treatment carries a described and an undescribed photo, so
      // dev:mock exercises both halves of the media list; the rest get the one
      // cover image every treatment really has.
      media: isDemoRecording()
        ? []
        : i === 0
          ? [
              {
                url: config.coverPicture,
                type: "image" as const,
                name: null,
                description:
                  "Treatment room with the laser device, ready for a lower-leg session",
              },
              {
                url: STORAX_TREATMENTS[1].coverPicture,
                type: "image" as const,
                name: null,
                description: null,
              },
            ]
          : [{ url: config.coverPicture, type: "image" as const, name: null }],
      externalId: null,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: timestamp,
      doctors: [], // Will be populated after doctors are generated
      // Seed the first two so dev:mock exercises both the populated list and
      // the empty state on the treatment page.
      faqs:
        i === 0
          ? [
              {
                id: createId(),
                question: "How many sessions will I need?",
                answer:
                  "Most people see a clear difference after 3 to 6 sessions, spaced 4 weeks apart. We confirm your plan at the free consultation.",
              },
              {
                id: createId(),
                question: "Is there any downtime?",
                answer:
                  "Mild redness for a few hours is normal. You can go back to your day straight away. Just skip make-up and direct sun until the next morning.",
              },
            ]
          : [],
      intakeConfig:
        i === 0
          ? {
              instructions:
                "Skip this if they already have a consultation booked. One question per message.",
              questions: [
                {
                  id: createId(),
                  question: "How long has this been going on?",
                },
                {
                  id: createId(),
                  question: "Have you had any treatment for it before?",
                },
                {
                  id: createId(),
                  question: "Which area are you most bothered by?",
                },
              ],
            }
          : { instructions: null, questions: [] },
      pageUrl: null,
      beforeAfter: [],
    });
  }

  return treatments;
}

/**
 * Update treatments with doctor associations
 */
export function updateTreatmentsWithDoctors(
  treatments: Treatment[],
  doctors: Array<{
    id: string;
    name: string;
    photo: string | null;
    treatments?: Array<{ id: string }>;
  }>,
): void {
  for (const treatment of treatments) {
    const treatmentDoctors: TreatmentDoctor[] = doctors
      .filter((d) => d.treatments?.some((t) => t.id === treatment.id))
      .map((d) => ({
        id: d.id,
        name: d.name,
        photo: d.photo,
      }));

    treatment.doctors = treatmentDoctors;
  }
}
